// Plattform-Erkennung für die Kunden-App (Spec v3 §6.1).
//
// Regel: Capacitor wird NUR hier abgefragt. Der übrige Code fragt isNative() und kennt
// den Unterschied zwischen Web und App sonst nicht.
//
// Bewusst ohne Import von @capacitor/core: Die native Hülle injiziert window.Capacitor
// selbst in den WebView. So bleibt der Web-Build unverändert groß, solange noch kein
// Plugin mit Events (Push) gebraucht wird. Einfache Aufrufe laufen über
// Capacitor.nativePromise(plugin, methode, optionen) der nativen Bridge — derselbe Weg,
// den @capacitor/core intern nutzt. Mit Push wechselt dieses Modul auf @capacitor/core,
// die Funktionen hier bleiben gleich.

type CapacitorGlobal = {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
  nativePromise?: (plugin: string, method: string, options?: Record<string, unknown>) => Promise<unknown>;
};

function capacitor(): CapacitorGlobal | undefined {
  if (typeof window === "undefined") return undefined;
  return (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor;
}

export type Platform = "ios" | "android" | "web";

export function isNative(): boolean {
  try {
    return capacitor()?.isNativePlatform?.() === true;
  } catch {
    return false;
  }
}

export function getPlatform(): Platform {
  if (!isNative()) return "web";
  const p = capacitor()?.getPlatform?.();
  return p === "ios" || p === "android" ? p : "web";
}

/** Ruft eine native Plugin-Methode auf. Im Web und bei Fehlern ein stiller No-op. */
export async function callNative(plugin: string, method: string, options: Record<string, unknown> = {}): Promise<void> {
  if (!isNative()) return;
  try {
    await capacitor()?.nativePromise?.(plugin, method, options);
  } catch {
    // Native Fehler dürfen die Seite nie brechen.
  }
}

/**
 * Ruft eine native Plugin-Methode auf und liefert das Ergebnis. Wirft im Web und bei Fehlern
 * (Plugin fehlt in der installierten App-Version, Abbruch durch den Nutzer). Für Abläufe mit
 * eigener Rückfallebene, z. B. Teilen einer Bilddatei (Monatsrückblick, Roadmap 2.5).
 */
export async function nativeAufrufen<T>(plugin: string, method: string, options: Record<string, unknown> = {}): Promise<T> {
  const cap = capacitor();
  if (!isNative() || !cap?.nativePromise) throw new Error("nicht in der App");
  return (await cap.nativePromise(plugin, method, options)) as T;
}

/** Ruft eine native Plugin-Methode auf und liefert das Ergebnis. Im Web und bei Fehlern: null. */
async function readNative<T>(plugin: string, method: string): Promise<T | null> {
  if (!isNative()) return null;
  try {
    return ((await capacitor()?.nativePromise?.(plugin, method, {})) ?? null) as T | null;
  } catch {
    return null;
  }
}

export type AppInfo = {
  /** Store-Version, z. B. „1.0“ */
  version: string | null;
  /** Native Build-Nummer, z. B. „2“ */
  build: string | null;
  /** Eingespieltes Capgo-Live-Update, z. B. „1.0.5“ (null = Web-Stand aus dem Store-Build) */
  bundle: string | null;
};

let appInfoPromise: Promise<AppInfo | null> | null = null;

/** Store-Version, Build und Capgo-Bundle der App. Im Web null. Einmal je App-Start gelesen. */
export function getAppInfo(): Promise<AppInfo | null> {
  if (!isNative()) return Promise.resolve(null);
  if (!appInfoPromise) {
    appInfoPromise = (async () => {
      const info = await readNative<{ version?: string; build?: string }>("App", "getInfo");
      const current = await readNative<{ bundle?: { version?: string } }>("CapacitorUpdater", "current");
      const bundle = current?.bundle?.version && current.bundle.version !== "builtin" ? current.bundle.version : null;
      return { version: info?.version || null, build: info?.build || null, bundle };
    })();
  }
  return appInfoPromise;
}

let appVersionPromise: Promise<string | null> | null = null;

/**
 * App-Version für die eigene Nutzungsmessung (Roadmap 1.11): Store-Version plus eingespieltes
 * Capgo-Live-Update, z. B. „1.0 (3) · 1.1.42“. Im Web null. Einmal je App-Start gelesen.
 */
export function getAppVersion(): Promise<string | null> {
  if (!isNative()) return Promise.resolve(null);
  if (!appVersionPromise) {
    appVersionPromise = getAppInfo().then((info) => {
      if (!info) return null;
      const store = info.version ? `${info.version}${info.build ? ` (${info.build})` : ""}` : null;
      const value = [store, info.bundle].filter(Boolean).join(" · ");
      return value ? value.slice(0, 40) : null;
    });
  }
  return appVersionPromise;
}

// Safe Areas (Notch/Dynamic Island oben, Home-Indikator unten) in der iOS-App.
//
// Bis Store-Build 1.0 läuft die Hülle mit ios.contentInset 'automatic': iOS rückt den Inhalt
// selbst unter der Statusleiste und über dem Home-Indikator ein
// (auch für position: fixed). Ab 1.0.1 (charlies-app #15) steht 'never', dann muss die
// Web-App den Abstand selbst halten. Die Einstellung steckt im nativen Build und ist per OTA
// nicht änderbar, das Web-Bundle läuft aber in beiden Hüllen. Deshalb hängen --safe-top und
// --safe-bottom (index.css) an der Store-Version: In iOS-Hüllen vor 1.0.1 setzt die Klasse
// `ios-inset-auto` beide auf 0, sonst gilt env(safe-area-inset-top/-bottom). Im Web und auf dem
// Desktop ist env() ohnehin 0, mobil im Browser die echte Notch-Höhe (viewport-fit=cover).
const INSET_AUTO_KLASSE = "ios-inset-auto";
const INSET_AUTO_SPEICHER = "cc_ios_inset_auto";
/** Erste Store-Version mit ios.contentInset 'never'. */
const INSET_NEVER_AB = [1, 0, 1];

function versionVor(version: string, grenze: number[]): boolean {
  const teile = version.split(".").map((t) => Number.parseInt(t, 10) || 0);
  for (let i = 0; i < grenze.length; i++) {
    const a = teile[i] ?? 0;
    if (a !== grenze[i]) return a < grenze[i];
  }
  return false;
}

function insetAutoSetzen(an: boolean): void {
  document.documentElement.classList.toggle(INSET_AUTO_KLASSE, an);
}

// iPad (Apple-Ablehnung 12.10.2026, Guideline 4, iPad Air 11" mit iPadOS 27): Die iPhone-App
// läuft dort nicht mehr im alten Kompatibilitätsrahmen, sondern in einem frei skalierbaren Fenster.
// Oben links liegen die Fensterknöpfe (rot/gelb/grün) ÜBER dem Inhalt, env(safe-area-inset-top)
// rechnet sie nicht ein (Simulator 14.10.: 0 px, Knöpfe etwa 18 bis 41 px unter der Oberkante
// des Web-Inhalts). Die Klasse `ipad-fenster` schiebt --safe-top (index.css) um eine Zeile nach
// unten, damit Kopfzeilen, Titel und Knöpfe darunter beginnen. Erkennung über die Kennung des
// WebViews: im iPad-Fenster meldet er „iPad“, auf dem iPhone nie.
const IPAD_FENSTER_KLASSE = "ipad-fenster";

function istIpad(): boolean {
  if (typeof navigator === "undefined") return false;
  if (/iPad/.test(navigator.userAgent)) return true;
  // iPad im Desktop-Modus meldet sich als Mac, hat aber Touch.
  return /Macintosh/.test(navigator.userAgent) && (navigator.maxTouchPoints ?? 0) > 1;
}

function ipadFensterSetzen(): void {
  document.documentElement.classList.toggle(IPAD_FENSTER_KLASSE, istIpad());
}

/**
 * Stellt beim Start ein, ob die App den oberen Safe-Area-Abstand selbst halten muss.
 * Sofort aus dem letzten bekannten Stand (kein Sprung beim Start), danach mit der echten
 * Store-Version abgeglichen. Ohne gespeicherten Stand gilt die Hülle als 'automatic'
 * (Store-Build 1.0). Im Web und auf Android ein No-op.
 */
export function safeAreaModusSetzen(): void {
  if (getPlatform() !== "ios") return;
  ipadFensterSetzen();
  let gemerkt: string | null = null;
  try {
    gemerkt = window.localStorage.getItem(INSET_AUTO_SPEICHER);
  } catch {
    // Speicher gesperrt: Standard 'automatic'
  }
  insetAutoSetzen(gemerkt !== "0");
  void getAppInfo().then((info) => {
    if (!info?.version) return;
    const auto = versionVor(info.version, INSET_NEVER_AB);
    insetAutoSetzen(auto);
    try {
      window.localStorage.setItem(INSET_AUTO_SPEICHER, auto ? "1" : "0");
    } catch {
      // egal, nächster Start gleicht wieder ab
    }
  });
}

/** Creme wie body/Kundenbereich (#F3EFE8). */
export const STATUSBAR_APP_FARBE = "#F3EFE8";

/**
 * Blendet den nativen Splash aus, sobald die App gerendert hat, und stellt danach die
 * Statusleiste auf Creme um. Während des Splashs ist sie blau wie der Startbildschirm
 * (StatusBar.backgroundColor in capacitor.config der App-Hülle); das Plugin legt auf iOS
 * eine eigene Farbfläche über den Splash, deshalb muss die Farbe hier nachgezogen werden.
 */
export async function hideSplash(): Promise<void> {
  if (!isNative()) return;
  await callNative("SplashScreen", "hide", { fadeOutDuration: 200 });
  // Erst nach dem Ausblenden umschalten, sonst blitzt Creme über dem blauen Splash auf.
  await new Promise((r) => setTimeout(r, 220));
  await callNative("StatusBar", "setBackgroundColor", { color: STATUSBAR_APP_FARBE });
}

/**
 * Meldet dem Capgo-Updater, dass das aktuelle Web-Bundle sauber gestartet ist.
 * Ohne diese Meldung innerhalb von 10 s rollt Capgo ein frisch eingespieltes Live-Update
 * automatisch auf das vorherige Bundle zurück (Schutz gegen kaputte OTA-Updates).
 */
export function notifyAppReady(): Promise<void> {
  return callNative("CapacitorUpdater", "notifyAppReady");
}

/** Öffnet eine URL im In-App-Browser (SFSafariViewController / Custom Tab). */
export function openInAppBrowser(url: string): Promise<void> {
  return callNative("Browser", "open", { url, presentationStyle: "popover" });
}

/** Öffentliche Website — Ziel für Seiten, die es in der App nicht gibt. */
export const WEBSITE_ORIGIN = "https://charliescrew.com";
